import React, { useState, useEffect } from "react";
import { Modal, Button, Label, Textarea } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";

const AiRestructureModal = ({ show, onClose, onConfirm, isLoading }) => {
  const [mode, setMode] = useState("default");
  const [customText, setCustomText] = useState("");

  useEffect(() => {
    if (show) {
      setMode("default");
      setCustomText("");
    }
  }, [show]);

  const handleSubmit = async () => {
    if (mode === "custom" && !customText.trim()) {
      return;
    }
    try {
      await Promise.resolve(
        onConfirm({
          extraUserInstructions:
            mode === "custom" ? customText.trim() : "",
        })
      );
    } catch (e) {
      // Parent usually handles errors; this prevents an unhandled rejection
      // if anything still throws (Safari / overlay: "Unknown promise rejection reason").
      console.error("Restructure with AI:", e);
    }
  };

  const customInvalid = mode === "custom" && !customText.trim();

  return (
    <Modal show={show} onClose={onClose} size="lg">
      <Modal.Header>Restructure with AI</Modal.Header>
      <Modal.Body className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Clean up crawled or messy content into clear headings and paragraphs.
          Default rules also use neutral third person (no “we offer”).
          Choose default rules or add your own instructions.
        </p>
        <fieldset className="space-y-3">
          <legend className="sr-only">Restructure mode</legend>
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 dark:border-gray-600">
            <input
              type="radio"
              name="ai-mode"
              className="mt-1"
              checked={mode === "default"}
              onChange={() => setMode("default")}
            />
            <span>
              <span className="font-medium text-gray-900 dark:text-white">
                Default
              </span>
              <span className="mt-1 block text-sm text-gray-500 dark:text-gray-400">
                Use the standard rules: headings, lists, strip crawl junk, JSON
                → sections, neutral third person, no invented facts.
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 dark:border-gray-600">
            <input
              type="radio"
              name="ai-mode"
              className="mt-1"
              checked={mode === "custom"}
              onChange={() => setMode("custom")}
            />
            <span className="w-full">
              <span className="font-medium text-gray-900 dark:text-white">
                Custom instructions
              </span>
              <span className="mt-1 block text-sm text-gray-500 dark:text-gray-400">
                Add how you want this specific piece edited (tone, sections,
                what to emphasize).
              </span>
            </span>
          </label>
        </fieldset>
        {mode === "custom" && (
          <div>
            <Label htmlFor="ai-custom-prompt" value="Your instructions" />
            <Textarea
              id="ai-custom-prompt"
              className="mt-1"
              rows={4}
              placeholder='e.g. "Use bullet points for requirements", "Keep only the deadline section", "Formal tone"'
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
            />
            {customInvalid && (
              <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">
                Enter instructions or switch to Default.
              </p>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button color="gray" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          color="purple"
          onClick={handleSubmit}
          disabled={isLoading || customInvalid}
          isProcessing={isLoading}
          processingSpinner={
            <AiOutlineLoading className="h-5 w-5 animate-spin" />
          }
        >
          Restructure
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AiRestructureModal;
